import { memo } from 'react';
import { useFormContext } from 'react-hook-form';
import { Divider, Label, RichTextRenderer } from '@knack/asterisk-react';

import {
  type SearchView,
  type SearchViewFilters,
  type SearchViewInput
} from '@/types/schema/views/SearchView';
import { shouldHideValueBasedOnOperator } from '@/utils/field-operators';
import { sanitizeHTML } from '@/utils/sanitizeHTML';
import { cn } from '@/utils/tailwind';
import { FormErrorMessage } from '@/components/errors/FormErrorMessage';
import { useViewContext } from '@/components/views/ViewContext';
import { KeywordSearchInput } from './inputs/KeywordSearchInput';
import { SearchFieldInput } from './inputs/SearchFieldInput';
import { SearchInstructions } from './inputs/SearchInstructions';
import { SearchFieldOperators } from './SearchFieldOperators';

type SearchInputProps = {
  input: SearchViewInput;
};

function SearchInput({ input }: SearchInputProps) {
  const {
    watch,
    getValues,
    formState: { errors }
  } = useFormContext<SearchViewFilters>();

  const { view, sourceTable } = useViewContext<SearchView>();

  const filterRuleIndex = watch('rules').findIndex((rule) => rule.inputId === input.id);

  if (input.field === 'keyword_search') {
    return <KeywordSearchInput filterRuleIndex={filterRuleIndex} errors={errors} input={input} />;
  }

  if (input.field === 'section_break') {
    const sanitizedInputCopyText = sanitizeHTML((input as SearchViewInput).copy);

    return (
      <div className="flex flex-col gap-2">
        {input.name && <h3 className="text-lg font-medium">{input.name}</h3>}
        {input.copy && (
          <RichTextRenderer dangerouslySetInnerHTML={{ __html: sanitizedInputCopyText }} />
        )}
      </div>
    );
  }

  if (input.type === 'divider') {
    return <Divider className="w-full py-2" />;
  }

  const criteriaField = [...sourceTable.fields, ...sourceTable.fieldsFromConnections].find(
    (field) => field.key === input.field
  );

  if (!criteriaField) {
    return null;
  }

  const hasError = !!errors.rules?.[filterRuleIndex];
  const criteriaOperator = getValues(`rules.${filterRuleIndex}.operator`);

  return (
    <div
      className={cn('flex gap-2', {
        'flex-col': view.label_format === 'top',
        'items-center': view.label_format === 'left' || view.label_format === 'right'
      })}
    >
      <Label
        isRequired={input.required}
        className={cn('flex items-center font-medium', {
          'text-destructive': hasError,
          'text-left': view.label_format === 'left' || view.label_format === 'top',
          'text-right': view.label_format === 'right',
          'w-1/4 min-w-24 max-w-52 truncate lg:min-w-32':
            view.label_format === 'right' || view.label_format === 'left'
        })}
      >
        {input.name || criteriaField.name}
      </Label>
      <div className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row">
        {!input.ignore_operators && (
          <SearchFieldOperators
            input={input}
            criteriaField={criteriaField}
            filterRuleIndex={filterRuleIndex}
          />
        )}

        <div className="flex min-w-0 grow flex-col justify-center">
          <SearchFieldInput
            field={criteriaField}
            input={input}
            filterRuleIndex={filterRuleIndex}
            intent={hasError ? 'destructive' : 'default'}
            criteriaOperator={criteriaOperator}
            isDisabled={shouldHideValueBasedOnOperator(criteriaOperator)}
          />
          {errors.rules && (
            <FormErrorMessage
              errors={errors}
              name={`rules.${filterRuleIndex}.value`}
              className="mt-1"
            />
          )}
          {input.instructions && <SearchInstructions>{input.instructions}</SearchInstructions>}
        </div>
      </div>
    </div>
  );
}

const memoized = memo(SearchInput);
export { memoized as SearchInput };
